/**
 * MaintenanceGate — Blende jede Nicht-Admin-Session vor dem Shop aus, wenn der
 * Wartungsmodus aktiv ist. Admin/Superadmin-Sessions (via `admin_token`) sehen
 * den Shop normal und können weiter im Backoffice arbeiten.
 *
 * Der Gate wird in App.js so weit oben wie möglich montiert, damit er auch vor
 * Router-Navigation greift. Admin-Routen (`/admin221542/*`) und das Login-Pfad
 * werden **nie** geblockt, damit der Betreiber auch im Wartungsmodus arbeiten
 * kann.
 */
import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Wrench, Mail, ArrowRight, ShieldCheck } from 'lucide-react';
import { useSeoTestMode } from './GlobalSeoGuard';

const ADMIN_ROUTES = ['/admin221542', '/admin', '/portal'];
const BYPASS_KEY = 'maintenance-bypass';

function isAdminSession() {
  try {
    if (typeof localStorage === 'undefined') return false;
    if (localStorage.getItem('admin_token')) return true;
    // Fallback: JWT im normalen `token`-Slot – wenn Rolle admin/superadmin → durchlassen.
    const t = localStorage.getItem('token');
    if (!t) return false;
    const parts = t.split('.');
    if (parts.length < 2) return false;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    const role = payload?.role || payload?.roles?.[0] || '';
    return ['admin', 'superadmin', 'staff'].includes(String(role).toLowerCase());
  } catch { return false; }
}

function hasBypassCookie() {
  try {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    if (params.get('preview') === '1') {
      sessionStorage.setItem(BYPASS_KEY, '1');
      return true;
    }
    return sessionStorage.getItem(BYPASS_KEY) === '1';
  } catch { return false; }
}

export default function MaintenanceGate({ children }) {
  const cfg = useSeoTestMode();
  const location = useLocation();

  const onAdminRoute = ADMIN_ROUTES.some((p) => location.pathname.startsWith(p));
  const isAuthPath = location.pathname === '/login' || location.pathname.startsWith('/login/');

  if (!cfg.maintenance_mode || onAdminRoute || isAuthPath || isAdminSession() || hasBypassCookie()) {
    return children;
  }

  const heading = cfg.maintenance_heading || 'Wir sind gleich wieder da.';
  const message = cfg.maintenance_message || 'Kurze Wartung – vielen Dank für deine Geduld.';
  const eta = cfg.maintenance_eta || '';
  const contact = cfg.maintenance_contact_email || '';
  const footerText = cfg.maintenance_footer_text || '';

  return (
    <div
      className="min-h-screen bg-[#faf5e6] text-stone-900 flex items-center justify-center px-4 py-14 relative overflow-hidden"
      data-testid="maintenance-gate"
    >
      {/* dezente Berlin-Gold-Aura */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-0 opacity-60"
          style={{
            background: 'radial-gradient(ellipse 60% 40% at 50% 0%, rgba(201,162,60,0.28) 0%, rgba(201,162,60,0.08) 45%, transparent 100%)',
          }}
        />
      </div>

      <div className="relative w-full max-w-2xl text-center">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white shadow-md border border-[#C9A23C]/30 mb-8">
          <Wrench className="w-9 h-9 text-[#8a6d10]" data-testid="maintenance-icon" />
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl leading-tight" data-testid="maintenance-heading">
          {heading}
        </h1>
        <p className="mt-4 text-lg text-stone-700 max-w-xl mx-auto" data-testid="maintenance-message">
          {message}
        </p>
        {eta && (
          <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#C9A23C]/30 text-sm text-stone-800" data-testid="maintenance-eta">
            <ArrowRight className="w-4 h-4 text-[#8a6d10]" />
            <span>Voraussichtlich wieder erreichbar: <b>{eta}</b></span>
          </div>
        )}
        {contact && (
          <div className="mt-8 text-sm text-stone-600" data-testid="maintenance-contact">
            <Mail className="inline w-4 h-4 mr-1.5 text-[#8a6d10]" />
            Kontakt: <a href={`mailto:${contact}`} className="underline decoration-[#C9A23C]/60 hover:decoration-[#C9A23C]">{contact}</a>
          </div>
        )}
        <div className="mt-12 flex items-center justify-center gap-6 text-xs text-stone-500">
          <Link to="/agb" className="hover:text-stone-800">AGB</Link>
          <span>·</span>
          <Link to="/datenschutz" className="hover:text-stone-800">Datenschutz</Link>
          <span>·</span>
          <Link to="/impressum" className="hover:text-stone-800">Impressum</Link>
        </div>
        {footerText && (
          <div className="mt-4 text-[10px] text-stone-400 flex items-center justify-center gap-1" data-testid="maintenance-footer-display">
            <ShieldCheck className="w-3 h-3" /> <span>{footerText}</span>
          </div>
        )}
      </div>
    </div>
  );
}
